/* eslint-disable react-hooks/exhaustive-deps */
import { API_STATUS } from 'core-lib/constants/network';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { LocalStorage } from 'core-lib/constants/storage';
import { useProfile } from 'core-lib/hooks/useProfile';
import { isServer } from 'core-lib/utils/utils';
import appendItemToCart from '../services/cart/appendItemToCart';
import deleteItemFromCart from '../services/cart/deleteItemFromCart';
import getCart, { serializeCartWithCartData } from '../services/cart/getCart';
import { updateCart } from '../store/action';
import { CartParent } from '../types';
import Cart, { CartItem } from '../types/cart';
import useParkplusStoreV2 from './useParkplusStoreV2';
import buyNowItem from '../services/cart/buyNow';

const useCart = (shouldFetchCart = false) => {
  // declarations
  const dispatch = useDispatch();
  const router = useRouter();

  // custom hooks
  const { cart, cartData, cartApiStatus } = useParkplusStoreV2();
  const { isAuthenticated } = useProfile();

  // constants
  const originPath = router.query.origin_path?.toString() || '';
  const { cartId } = cartData ?? {};

  // update cart items in store
  const updateCartAfterApiCall = (response: Cart, payload: CartParent) => {
    if (response) {
      payload = {
        data: response,
        apiStatus: isAuthenticated ? API_STATUS.success : API_STATUS.default,
        apiError: '',
      };
    } else {
      payload = {
        ...cart,
        apiStatus: isAuthenticated ? API_STATUS.failure : API_STATUS.default,
      };
    }
    dispatch(updateCart(payload));
  };

  // fetches cart and updates its store
  const fetchCart = async () => {
    let payload: CartParent = {
      ...cart,
      apiStatus: API_STATUS.in_progress,
    };

    dispatch(updateCart(payload));

    const response: Cart = await getCart(originPath);

    updateCartAfterApiCall(response, payload);
  };

  const updateCartFromLocalStorage = () => {
    const cart: Cart = JSON.parse(
      localStorage.getItem(LocalStorage.ParkplusStoreV2Cart)
    );
    const payload: CartParent = {
      apiStatus: API_STATUS.default,
      data: cart,
      apiError: null,
    };
    dispatch(updateCart(payload));
  };

  const getAndSetCart = () => {
    if (isServer()) {
      return;
    }
    if (shouldFetchCart && cartApiStatus === API_STATUS.default) {
      if (isAuthenticated) {
        fetchCart();
      } else {
        updateCartFromLocalStorage();
      }
    }
  };

  const updateBECart = async (body: CartItem, isBuyNow: boolean = false) => {
    const response = isBuyNow
      ? buyNowItem(body, cartId)
      : body.quantity > 0
      ? await appendItemToCart(body, cartId)
      : await deleteItemFromCart(body);

    return response;
  };

  const updateCartInLocalStorage = (body: CartItem) => {
    const cart: Cart = JSON.parse(
      localStorage.getItem(LocalStorage.ParkplusStoreV2Cart)
    );
    let newCartData: CartItem[] = [];
    let totalCartValue = 0;
    if (body.quantity > 0) {
      let isQuantityUpdated = false;
      newCartData =
        cart?.data?.map((item) => {
          if (item.productSku === body.productSku) {
            isQuantityUpdated = true;
            // change
            item.quantity = body.quantity;
            totalCartValue += item.quantity * item.discountedPrice;
            return item;
          } else {
            totalCartValue += item.quantity * item.discountedPrice;
            return item;
          }
        }) ?? [];

      if (!isQuantityUpdated) {
        totalCartValue += body.quantity * body.discountedPrice;
        newCartData.push(body);
      }
    } else {
      newCartData = cart?.data?.filter((e) => {
        if (e?.productId !== body?.productId) {
          totalCartValue += e.quantity * e.discountedPrice;
          return true;
        }
        return false;
      });
    }
    const response = serializeCartWithCartData({}, newCartData, totalCartValue);
    localStorage.setItem(
      LocalStorage.ParkplusStoreV2Cart,
      JSON.stringify(response)
    );
    return response;
  };

  // adds/removes cart items and updates its store
  const handleUpdateCart = async (
    body: CartItem,
    isBuyNow: boolean = false
  ) => {
    let payload: CartParent = {
      ...cart,
      data: {
        ...cartData,
        lastSelectedProductSku: body.productSku,
      },
      apiStatus: API_STATUS.in_progress,
    };

    dispatch(updateCart(payload));

    const response = isAuthenticated
      ? await updateBECart(body, isBuyNow)
      : updateCartInLocalStorage(body);

    updateCartAfterApiCall(response, payload);
  };

  useEffect(() => {
    getAndSetCart();
  }, [cartApiStatus, isAuthenticated]);

  return {
    fetchCart,
    handleUpdateCart,
  };
};

export default useCart;
